import React, { useState, useRef } from "react";
import axios from "axios";
import { X, Upload, CheckCircle2, ImagePlus, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SLOTS_BY_KIND = {
    center: [
        { key: "front_day",   label: "360° · Front · Day",   kind: "panorama", view: "front", mode: "day" },
        { key: "front_night", label: "360° · Front · Night", kind: "panorama", view: "front", mode: "night" },
        { key: "rear_day",    label: "360° · Rear · Day",    kind: "panorama", view: "rear",  mode: "day" },
        { key: "rear_night",  label: "360° · Rear · Night",  kind: "panorama", view: "rear",  mode: "night" },
        { key: "iso_day",     label: "ISO · Aerial · Day",   kind: "iso",      view: "iso",   mode: "day" },
        { key: "iso_night",   label: "ISO · Aerial · Night", kind: "iso",      view: "iso",   mode: "night" },
    ],
    basic: [
        { key: "front_day",   label: "360° · Day",   kind: "panorama", view: "front", mode: "day" },
        { key: "front_night", label: "360° · Night", kind: "panorama", view: "front", mode: "night" },
        { key: "iso_day",     label: "ISO · Day",    kind: "iso",      view: "iso",   mode: "day" },
        { key: "iso_night",   label: "ISO · Night",  kind: "iso",      view: "iso",   mode: "night" },
    ],
    hallway: [
        { key: "hallway_day",   label: "Hallway · Day",   kind: "panorama", view: "hallway", mode: "day" },
        { key: "hallway_night", label: "Hallway · Night", kind: "panorama", view: "hallway", mode: "night" },
        { key: "interior_day",  label: "Interior · Day",  kind: "panorama", view: "interior", mode: "day" },
        { key: "interior_night",label: "Interior · Night",kind: "panorama", view: "interior", mode: "night" },
        { key: "iso_day",       label: "ISO · Day",       kind: "iso",      view: "iso",     mode: "day" },
        { key: "iso_night",     label: "ISO · Night",     kind: "iso",      view: "iso",     mode: "night" },
    ],
};

const slotsForSection = (section) => SLOTS_BY_KIND[section?.kind] || SLOTS_BY_KIND.basic;

/**
 * Reusable upload panel — supports drag-drop and click to select files.
 * Uploads in bulk via /api/sections/{id}/upload-bulk.
 */
export default function UploadPanel({ section, isNight, onUpdated, onClose, embedded = false, initialView = null }) {
    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const visibleSlots = slotsForSection(section);
    const focusView = initialView; // only highlight this view's slots

    const [files, setFiles] = useState({}); // { slotKey: File }
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const setFile = (key, file) => {
        setFiles((prev) => ({ ...prev, [key]: file }));
        setError(null);
        setSuccess(false);
    };

    const clearFile = (key) => {
        setFiles((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const handleSubmit = async () => {
        const entries = Object.entries(files);
        if (entries.length === 0) {
            setError("Add at least one image to upload.");
            return;
        }
        setUploading(true);
        setError(null);
        setProgress(0);

        try {
            const fd = new FormData();
            entries.forEach(([key, file]) => fd.append(key, file));
            const res = await axios.post(
                `${API}/sections/${section.id}/upload-bulk`,
                fd,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (e) => {
                        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                    },
                }
            );
            onUpdated && onUpdated(res.data);
            setFiles({});
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2500);
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.detail || err.message || "Upload failed";
            setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    const totalFiles = Object.keys(files).length;

    return (
        <div
            data-testid={`upload-panel-${section.id}`}
            className={`${isNight ? "glass" : "glass-day"} rounded-2xl p-5 md:p-6 text-left w-full max-w-2xl`}
            style={{ border: `1px solid ${accent}33` }}
        >
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: accent }}>
                        Upload Images · {section.label}
                    </div>
                    <div
                        className="text-xs mt-1 opacity-70"
                        style={{ color: isNight ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }}
                    >
                        {focusView
                            ? `Replacing ${focusView} images. Other slots remain available.`
                            : "Drop your 360° equirectangular panoramas and ISO aerial renders."}
                    </div>
                </div>
                {onClose && (
                    <button
                        type="button"
                        data-testid={`upload-panel-close-${section.id}`}
                        onClick={onClose}
                        style={{ color: isNight ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }}
                    >
                        <X size={18} />
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {visibleSlots.map((slot) => (
                    <DropSlot
                        focused={focusView ? slot.view === focusView : false}
                        dimmed={focusView ? slot.view !== focusView : false}
                        key={slot.key}
                        slot={slot}
                        section={section}
                        file={files[slot.key]}
                        onPick={(f) => setFile(slot.key, f)}
                        onClear={() => clearFile(slot.key)}
                        accent={accent}
                        isNight={isNight}
                    />
                ))}
            </div>

            {error && (
                <div className="mt-4 text-xs" style={{ color: "#ff7a7a" }} data-testid={`upload-error-${section.id}`}>
                    {error}
                </div>
            )}

            {uploading && (
                <div className="mt-4">
                    <div
                        className="h-1 w-full rounded-full overflow-hidden"
                        style={{ background: isNight ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }}
                    >
                        <div
                            className="h-full transition-all"
                            style={{ width: `${progress}%`, background: accent }}
                        />
                    </div>
                    <div
                        className="mt-2 text-[10px] tracking-[0.25em] uppercase"
                        style={{ color: isNight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)" }}
                    >
                        Uploading · {progress}%
                    </div>
                </div>
            )}

            <div className="mt-5 flex items-center justify-between gap-3">
                <span
                    className="text-[10px] tracking-[0.25em] uppercase"
                    style={{ color: isNight ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)" }}
                >
                    {totalFiles > 0 ? `${totalFiles} file${totalFiles > 1 ? "s" : ""} selected` : "Select files above"}
                </span>
                <button
                    data-testid={`upload-submit-${section.id}`}
                    onClick={handleSubmit}
                    disabled={uploading || totalFiles === 0}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] tracking-[0.3em] uppercase transition-all disabled:opacity-40"
                    style={{ background: accent, color: "#0a0a0a", boxShadow: `0 10px 24px -10px ${accent}80` }}
                >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> :
                     success ? <CheckCircle2 size={14} /> : <Upload size={14} />}
                    {uploading ? "Uploading…" : success ? "Saved" : "Upload"}
                </button>
            </div>

            {!embedded && (
                <p
                    className="mt-4 text-[10px] tracking-[0.2em] uppercase"
                    style={{ color: isNight ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.5)" }}
                >
                    Accepts .png .jpg .webp · up to ~25MB each
                </p>
            )}
        </div>
    );
}

function DropSlot({ slot, section, file, onPick, onClear, accent, isNight, focused = false, dimmed = false }) {
    const inputRef = useRef(null);
    const [over, setOver] = useState(false);

    // Show existing image preview if already uploaded
    const existingUrl = slot.kind === "iso"
        ? section?.iso?.[slot.mode]
        : section?.panoramas?.[slot.view]?.[slot.mode];

    const handleFiles = (list) => {
        if (!list || list.length === 0) return;
        onPick(list[0]);
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        handleFiles(e.dataTransfer.files);
    };

    const previewSrc = file ? URL.createObjectURL(file) : existingUrl;

    const borderColor = focused ? accent : (file ? accent : accent + "55");
    const borderStyle = (over || focused) ? "solid" : "dashed";

    return (
        <div
            data-testid={`slot-${section.id}-${slot.key}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
            className="relative rounded-xl p-3 cursor-pointer transition-all overflow-hidden group"
            style={{
                background: over
                    ? (isNight ? `rgba(212,175,55,0.10)` : `rgba(184,155,94,0.18)`)
                    : focused
                        ? (isNight ? `rgba(212,175,55,0.08)` : `rgba(184,155,94,0.14)`)
                        : (isNight ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
                border: `${focused ? 2 : 1}px ${borderStyle} ${borderColor}`,
                minHeight: 100,
                opacity: dimmed ? 0.5 : 1,
                boxShadow: focused ? `0 0 22px ${accent}55` : "none",
            }}
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/avif"
                className="hidden"
                data-testid={`file-input-${section.id}-${slot.key}`}
                onChange={(e) => handleFiles(e.target.files)}
            />

            {previewSrc && (
                <div
                    className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity"
                    style={{
                        backgroundImage: `url(${previewSrc})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        filter: "blur(0px) saturate(0.9)",
                    }}
                />
            )}

            <div className="relative flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div
                        className="text-[10px] tracking-[0.22em] uppercase truncate"
                        style={{ color: accent }}
                    >
                        {slot.label}
                    </div>
                    <div
                        className="text-[11px] mt-1 truncate"
                        style={{ color: isNight ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.85)" }}
                    >
                        {file
                            ? `📎 ${file.name}`
                            : existingUrl
                                ? "✓ Replace existing"
                                : "Drop or click to add"}
                    </div>
                </div>

                <div className="shrink-0">
                    {file ? (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onClear(); }}
                            className="w-7 h-7 rounded-full grid place-items-center"
                            style={{ background: accent, color: "#0a0a0a" }}
                            data-testid={`slot-clear-${section.id}-${slot.key}`}
                        >
                            <X size={12} />
                        </button>
                    ) : (
                        <div
                            className="w-7 h-7 rounded-full grid place-items-center"
                            style={{
                                background: existingUrl ? `${accent}30` : "transparent",
                                border: `1px solid ${accent}60`,
                                color: accent,
                            }}
                        >
                            <ImagePlus size={12} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
